import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import ContentCard from "@/components/media/ContentCard";
import LiveChannelCard from "@/components/media/LiveChannelCard";
import SEO from "@/components/seo/SEO";
import { Search } from "lucide-react";

const TYPES = [
  { k: "all", l: "All" },
  { k: "original", l: "Originals" },
  { k: "movie", l: "Movies" },
  { k: "series", l: "TV Shows" },
  { k: "documentary", l: "Documentaries" },
  { k: "podcast", l: "Podcasts" },
  { k: "comedy", l: "Comedy" },
  { k: "lifestyle", l: "Lifestyle" },
  { k: "live", l: "Live" },
  { k: "kids", l: "Kids" },
];

// SEO metadata per landing type — unique title + description per route
const SEO_MAP = {
  all:         { path: "/browse",    title: "Browse All",        desc: "Browse the full Your Channel Network catalog — original series, movies, documentaries, podcasts, live TV, and kids programming from a woman-owned streaming network." },
  original:    { path: "/originals", title: "YCN Originals",     desc: "Explore YCN Originals — exclusive original series and programming produced by Your Channel Network including Poolside Cooking." },
  movie:       { path: "/movies",    title: "Movies",            desc: "Watch movies on Your Channel Network — drama, suspense, indie films, and cinematic storytelling from independent filmmakers." },
  series:      { path: "/shows",     title: "TV Shows",          desc: "Discover TV shows and series on Your Channel Network — reality, talk shows, documentary series, and original episodic programming." },
  documentary: { path: "/browse",    title: "Documentaries",     desc: "Real stories and documentaries on Your Channel Network — entrepreneurship, culture, community, and inspiring true-story filmmaking." },
  podcast:     { path: "/browse",    title: "Podcasts",          desc: "Listen to podcasts on Your Channel Network — conversations, culture, entertainment, and creator-led shows." },
  comedy:      { path: "/browse",    title: "Comedy",            desc: "Comedy on Your Channel Network — stand-up, sketches, and comedic series from independent creators." },
  lifestyle:   { path: "/browse",    title: "Lifestyle",         desc: "Lifestyle programming on Your Channel Network — food, culture, wellness, cooking, and lifestyle entertainment." },
  live:        { path: "/live",      title: "Live TV",           desc: "Watch live channels on Your Channel Network. Streaming live entertainment, culture, and original programming." },
  kids:        { path: "/kids",      title: "Kids & Family",     desc: "Family-friendly and kids programming on Your Channel Network — safe entertainment for the whole family." },
};

export default function Browse({ initialType = "all" }) {
  const [titles, setTitles] = useState([]);
  const [type, setType] = useState(initialType);
  const [q, setQ] = useState("");

  useEffect(() => { setType(initialType); }, [initialType]);
  useEffect(() => { api.get("/titles?limit=200").then(({ data }) => setTitles(data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    let out = titles;
    if (type !== "all") out = out.filter((t) => t.type === type);
    if (q.trim()) {
      const s = q.toLowerCase();
      out = out.filter((t) => t.title.toLowerCase().includes(s) || (t.genres || []).some((g) => g.toLowerCase().includes(s)));
    }
    // Titles with real cover art appear first; gradient-fallback titles come after.
    return [...out].sort((a, b) => (b.poster_url ? 1 : 0) - (a.poster_url ? 1 : 0));
  }, [titles, type, q]);

  return (
    <div data-testid="browse-page" className="pt-24 md:pt-28 px-6 md:px-12 min-h-[80vh]">
      <SEO
        title={SEO_MAP[initialType]?.title || "Browse"}
        description={SEO_MAP[initialType]?.desc || SEO_MAP.all.desc}
        path={SEO_MAP[initialType]?.path || "/browse"}
      />
      <div className="max-w-[1400px] mx-auto">
        <header className="flex flex-col md:flex-row md:items-end gap-6 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-2">Discover</div>
            <h1 className="font-display text-4xl md:text-5xl font-black">Browse <span className="ycn-gradient-text">YCN</span></h1>
          </div>
          <div className="relative w-full md:w-96">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
            <input data-testid="browse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, genres…"
              aria-label="Search titles and genres"
              className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none pl-11 pr-5 py-3 text-white placeholder:text-white/40" />
          </div>
        </header>

        <nav aria-label="Content type filters" className="mt-6 flex gap-2 flex-wrap">
          {TYPES.map((t) => (
            <button key={t.k} onClick={() => setType(t.k)} data-testid={`filter-${t.k}`}
              aria-pressed={type === t.k}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${type === t.k ? "bg-white text-black border-white" : "bg-white/5 text-white/70 hover:text-white border-white/15"}`}>
              {t.l}
            </button>
          ))}
        </nav>

        <section aria-label="Browse results" className="mt-8">
          {(() => {
            if (filtered.length === 0) {
              return (
                <div className="ycn-glass rounded-3xl p-14 text-center">
                  <div className="font-display text-xl font-bold">No results.</div>
                  <p className="text-white/60 mt-1">Try a different filter or search term.</p>
                </div>
              );
            }
            if (type === "live") {
              return (
                <div className="flex flex-wrap gap-4">
                  {filtered.map((t) => <LiveChannelCard key={t.id} channel={t} />)}
                </div>
              );
            }
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filtered.map((t) => (
                  <article key={t.id}>
                    <ContentCard title={t} showBadge />
                  </article>
                ))}
              </div>
            );
          })()}
        </section>
      </div>
    </div>
  );
}
